import React, { createRef, useEffect } from "react";
import styled from "@emotion/styled";
import { matchPath, useNavigate } from "react-router-dom";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useDimensionsRef, useWindowSize } from "rooks";
import {
  ComponentMStyling,
  ComponentSStyling,
  TextStyle,
} from "../../../utils/styles/typography";
import { BREAKPOINTS } from "../../../utils/styles/breakpoints";
import { COLORS } from "../../../utils/styles/colors";
import { shouldHandleButtonPress } from "../../../utils/helpers";
// import { selectCurrentProject, selectProjects } from "../../../utils";
// import { useSelector } from "react-redux";
// import { current } from "@reduxjs/toolkit";

const NavigationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
  padding: 14px 16px 14px 16px;
  ${ComponentSStyling(TextStyle.Medium, COLORS.White)};

  ${BREAKPOINTS.LARGE} {
    ${ComponentMStyling(TextStyle.Medium, COLORS.White)};
    padding: 12px 20px 12px 20px;
  }

  &:hover {
    color: ${COLORS.White};
  }

  &:active {
    background-color: ${COLORS.Neutral50};
    color: ${COLORS.Primary900};
  }

  &.active {
    color: ${COLORS.White};
  }
`;

const Container = styled.nav`
  display: flex;
  flex-direction: column;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const ActiveBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  left: 0;
  right: 0;
  top: calc(48px + 8px + 8px);
`;

const ActiveBar = styled.div`
  position: absolute;
  bottom: 10px;
  height: 2px;
  background-color: ${COLORS.CustomSkin};
  border-radius: 1px;
  width: 40px;
  transition: all 0.2s ease-in-out;
  pointer-events: none;
`;

export const NavigationMenu = () => {
  const { t, i18n } = useTranslation();

  const barRef = createRef<HTMLDivElement>();
  const navigate = useNavigate();
  const location = useLocation();

  const { innerWidth } = useWindowSize();
  const [hoveredItem, setHoveredItem] = React.useState<HTMLDivElement>();

  const [ref, dimensions] = useDimensionsRef();

  // const projects = useSelector(selectProjects);

  const isPathActive = (path: string) => {
    const match = matchPath(path, location.pathname);
    return match !== null;
  };

  const handleNavigationClick = (path: string) => {
    navigate(path);
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    setHoveredItem(event.currentTarget as HTMLDivElement);
  };
  const handleMouseLeave = (event: React.MouseEvent) => {
    setHoveredItem(undefined);
  };

  useEffect(() => {
    if (barRef.current) {
      const item =
        hoveredItem?.getBoundingClientRect() ??
        (
          document.querySelector(".active.nav") as HTMLDivElement
        )?.getBoundingClientRect();
      const bar = barRef.current;
      if (item) {
        // Check if item exists before using its properties
        const rect = item; // No need to reassign if item is valid
        bar.style.left = `${rect.left + (rect.right - rect.left) / 2 - 20}px`;
      }
    }
  }, [
    hoveredItem,
    location,
    innerWidth,
    i18n.language,
    dimensions?.width,
    dimensions?.height,
    barRef,
  ]);

  return (
    <Container ref={ref}>
      <ItemsContainer>
        <NavigationItem
          tabIndex={0}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={isPathActive("/") ? "active nav" : ""}
          onClick={() => handleNavigationClick("/")}
          onKeyDown={(e) =>
            shouldHandleButtonPress(e) && handleNavigationClick("/")
          }
        >
          {t("Navigation.Home")}
        </NavigationItem>
        {/* <NavigationItem
          tabIndex={0}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={isPathActive("/project/*") ? "active nav" : ""}
          onClick={() =>
            handleNavigationClick(`/project/${projects[0]?.slug.current}`)
          }
          onKeyDown={(e) =>
            shouldHandleButtonPress(e) &&
            handleNavigationClick(`/project/${projects[0]?.slug.current}`)
          }
        >
          {t("Navigation.Apartments")}
        </NavigationItem> */}
        <NavigationItem
          tabIndex={0}
          className={isPathActive("/blocks") ? "active nav" : ""}
          onClick={() => handleNavigationClick("/blocks")}
          onKeyDown={(e) =>
            shouldHandleButtonPress(e) && handleNavigationClick("/blocks")
          }
        >
          {t("Navigation.Blocks")}
        </NavigationItem>
        <NavigationItem
          tabIndex={0}
          className={isPathActive("/gallery") ? "active nav" : ""}
          onClick={() => handleNavigationClick("/gallery")}
          onKeyDown={(e) =>
            shouldHandleButtonPress(e) && handleNavigationClick("/gallery")
          }
        >
          {t("Navigation.Gallery")}
        </NavigationItem>
        <NavigationItem
          tabIndex={0}
          className={isPathActive("/aboutproject") ? "active nav" : ""}
          onClick={() => handleNavigationClick("/aboutproject")}
          onKeyDown={(e) =>
            shouldHandleButtonPress(e) && handleNavigationClick("/aboutproject")
          }
        >
          {t("Navigation.AboutProject")}
        </NavigationItem>
      </ItemsContainer>
      <ActiveBarContainer>
        <ActiveBar ref={barRef} />
      </ActiveBarContainer>
    </Container>
  );
};
